import { z } from 'zod';
import { MEDIA_FILE_PATTERN, specRowSchema } from './catalog.js';
import { emailSchema } from './identity.js';

/**
 * The public storefront surface (docs/STOREFRONT.md): what a shopper on a tenant's store host can
 * read and do. Everything here is tenant-safe: the tenant comes from the Host, never from input,
 * and nothing in these shapes identifies another tenant, Avrahami's SAP data or the platform.
 *
 * Stores take requests for a quote, not orders (adr/0022): a shopper sends a list of products and
 * quantities, and the store answers with its price outside the platform. There is no payment. A
 * shopper reads a price here only in a store whose settings show prices (adr/0024); in every
 * other store each price field is null. The request is still called an "order" in the code, the
 * routes and the tables.
 * Enum values are shared with the database enums; a test checks that they match exactly.
 */

// ---------------------------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------------------------

/**
 * A quote request's lifecycle. The storefront creates NEW requests; the store moves them in the
 * tenant admin: IN_PROGRESS while it prepares the quote, QUOTED once the quote went to the
 * shopper, CLOSED when the matter is done, CANCELLED when it will not be answered.
 */
export const ORDER_STATUSES = ['NEW', 'IN_PROGRESS', 'QUOTED', 'CLOSED', 'CANCELLED'] as const;
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const FULFILLMENT_METHODS = ['DELIVERY', 'PICKUP'] as const;
export const fulfillmentMethodSchema = z.enum(FULFILLMENT_METHODS);
export type FulfillmentMethod = z.infer<typeof fulfillmentMethodSchema>;

export const CUSTOMER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export const customerStatusSchema = z.enum(CUSTOMER_STATUSES);
export type CustomerStatus = z.infer<typeof customerStatusSchema>;

/** The content pages a store can have (spec §04 "עמודי תוכן"). */
export const CONTENT_PAGE_KINDS = [
  'ABOUT',
  'CONTACT',
  'FAQ',
  'TERMS',
  'PRIVACY',
  'ACCESSIBILITY',
  'CUSTOM',
] as const;
export const contentPageKindSchema = z.enum(CONTENT_PAGE_KINDS);
export type ContentPageKind = z.infer<typeof contentPageKindSchema>;

export const STOREFRONT_SORTS = ['newest', 'name'] as const;
export const storefrontSortSchema = z.enum(STOREFRONT_SORTS);
export type StorefrontSort = z.infer<typeof storefrontSortSchema>;

/** Why a cart line cannot be requested as it is. */
export const CART_PROBLEMS = ['UNAVAILABLE', 'INSUFFICIENT_STOCK'] as const;
export const cartProblemSchema = z.enum(CART_PROBLEMS);
export type CartProblem = z.infer<typeof cartProblemSchema>;

/** Where a popup appears on the storefront. */
export const POPUP_PLACEMENTS = ['ALL_PAGES', 'HOME'] as const;
export const popupPlacementSchema = z.enum(POPUP_PLACEMENTS);
export type PopupPlacement = z.infer<typeof popupPlacementSchema>;

/**
 * What lowered an order's price when stores took orders. Nothing writes it now (adr/0022); the
 * database type and `order_adjustments` are kept.
 */
export const ADJUSTMENT_KINDS = ['PROMOTION', 'COUPON'] as const;
export const adjustmentKindSchema = z.enum(ADJUSTMENT_KINDS);
export type AdjustmentKind = z.infer<typeof adjustmentKindSchema>;

// ---------------------------------------------------------------------------------------------
// Store, theme, catalog
// ---------------------------------------------------------------------------------------------

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i);

/**
 * The store's colours, applied as CSS variables by the storefront. The defaults are the existing
 * store design (spec §12: the stores are based on the existing design); a store cannot change
 * them in its admin.
 */
export const storeThemeSchema = z.object({
  brand: hexColor,
  brandDark: hexColor,
  brandLight: hexColor,
  brandFaint: hexColor,
  accent: hexColor,
  topBar: hexColor,
});
export type StoreTheme = z.infer<typeof storeThemeSchema>;

export const DEFAULT_STORE_THEME: StoreTheme = {
  brand: '#1b2855',
  brandDark: '#03071a',
  brandLight: '#f2f5ff',
  brandFaint: '#fafbff',
  accent: '#405fc9',
  topBar: '#f4f6fd',
};

/**
 * The store's design (adr/0034, adr/0044): one of the platform's templates, and what the platform
 * set to make it this store's own. The colours themselves are `theme`; `ink` is the dark colour of
 * the template's dark surfaces (top strip, footer, closing panels).
 *
 * Five templates, each a whole store of its own (header, opening, sections, footer, product page):
 * - PANORAMA (1): steel and navy, a full-screen opening with a strip of the departments
 * - ATELIER (2): an editorial magazine: paper, terracotta and olive, hairlines, a numbered index
 * - DEPOT (3): a bold wholesale depot: black, concrete and safety yellow, aisle codes
 * - FRESH (4): friendly and rounded: mint, green and coral, a bento opening
 * - PLANNER (5): light and solution-led: white, warm grey and bordeaux, a kitchen-flow browser
 */
export const STORE_TEMPLATES = ['PANORAMA', 'ATELIER', 'DEPOT', 'FRESH', 'PLANNER'] as const;
export const storeTemplateSchema = z.enum(STORE_TEMPLATES);
export type StoreTemplate = z.infer<typeof storeTemplateSchema>;

/** The corners of cards, buttons and frames: square, slightly rounded, or round. */
export const STORE_CORNERS = ['SHARP', 'SOFT', 'ROUND'] as const;
export const storeCornersSchema = z.enum(STORE_CORNERS);
export type StoreCorners = z.infer<typeof storeCornersSchema>;

/**
 * The store's type: INDUSTRIAL (Secular One headings, IBM Plex Sans Hebrew text), MODERN (Rubik),
 * CLASSIC (Frank Ruhl Libre headings, Assistant text), HEEBO (Heebo), ASSISTANT (Assistant),
 * NOTO (Noto Serif Hebrew headings, Noto Sans Hebrew text), SUEZ (Suez One headings, Assistant
 * text) or KARANTINA (Karantina headings, Noto Sans Hebrew text).
 */
export const STORE_TYPEFACES = [
  'INDUSTRIAL',
  'MODERN',
  'CLASSIC',
  'HEEBO',
  'ASSISTANT',
  'NOTO',
  'SUEZ',
  'KARANTINA',
] as const;
export const storeTypefaceSchema = z.enum(STORE_TYPEFACES);
export type StoreTypeface = z.infer<typeof storeTypefaceSchema>;

export const storeDesignSchema = z.object({
  template: storeTemplateSchema,
  corners: storeCornersSchema,
  typeface: storeTypefaceSchema,
  ink: hexColor,
});
export type StoreDesign = z.infer<typeof storeDesignSchema>;

export const DEFAULT_STORE_DESIGN: StoreDesign = {
  template: 'PANORAMA',
  corners: 'SOFT',
  typeface: 'INDUSTRIAL',
  ink: '#171b21',
};

export const contentPageLinkSchema = z.object({
  slug: z.string(),
  kind: contentPageKindSchema,
  title: z.string(),
});
export type ContentPageLink = z.infer<typeof contentPageLinkSchema>;

export const storefrontImageSchema = z.object({
  file: z.string().regex(MEDIA_FILE_PATTERN),
  alt: z.string(),
});
export type StorefrontImage = z.infer<typeof storefrontImageSchema>;

/**
 * A link a store owner sets on a button (banner, popup): a path on the store itself (`/c/...`)
 * or an https address. Nothing else (no `javascript:`, no protocol-relative `//host`).
 */
export const STORE_LINK_PATTERN = /^(?:\/(?![/\\])[^\s\\]*|https:\/\/[^\s\\/]+(?:\/[^\s\\]*)?)$/;
export const storeLinkSchema = z
  .string()
  .trim()
  .max(300)
  .regex(STORE_LINK_PATTERN, 'a path on the store (starting with /) or an https:// address');

/** A popup the shopper sees once (dismissal is remembered per popup and version). */
export const storefrontPopupSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  title: z.string(),
  body: z.string(),
  image: storefrontImageSchema.nullable(),
  buttonLabel: z.string().nullable(),
  link: z.string().nullable(),
  placement: popupPlacementSchema,
});
export type StorefrontPopup = z.infer<typeof storefrontPopupSchema>;

/** Public, tenant-safe store information. */
export const storefrontStoreSchema = z.object({
  name: z.string(),
  /**
   * The address shoppers should use: the store's primary domain while it is active, its system
   * address otherwise. The storefront redirects every other address of the store to it.
   */
  primaryHost: z.string(),
  theme: storeThemeSchema,
  design: storeDesignSchema,
  logo: storefrontImageSchema.nullable(),
  favicon: storefrontImageSchema.nullable(),
  pages: z.array(contentPageLinkSchema),
  /**
   * What the store's settings allow shoppers to do (the API enforces the same rules).
   * `ordersEnabled`: the store takes quote requests now.
   */
  ordering: z.object({
    ordersEnabled: z.boolean(),
    guestCheckout: z.boolean(),
    registration: z.boolean(),
    delivery: z.boolean(),
    pickup: z.boolean(),
    pickupAddress: z.string().nullable(),
  }),
  /** The store shows its list prices to shoppers (its settings, adr/0024). */
  showPrices: z.boolean(),
  /** The store's own business details (footer, contact, the call and WhatsApp buttons). */
  contact: z.object({
    businessName: z.string().nullable(),
    phone: z.string().nullable(),
    /** The number shoppers write to on WhatsApp: the store's WhatsApp number, or else its phone. */
    whatsapp: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
  }),
  /** The home page's own title and description; null falls back to the store name. */
  seo: z.object({ title: z.string().nullable(), description: z.string().nullable() }),
  /** The store's own measurement IDs (spec §04 "אנליטיקס משלו"); validated formats only. */
  analytics: z.object({
    ga4MeasurementId: z.string().nullable(),
    metaPixelId: z.string().nullable(),
  }),
  /** Popups active right now. */
  popups: z.array(storefrontPopupSchema),
});
export type StorefrontStore = z.infer<typeof storefrontStoreSchema>;

/** A visible category with the number of visible products in it and below it. */
export const storefrontCategorySchema = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  name: z.string(),
  position: z.number().int(),
  productCount: z.number().int().min(0),
  /**
   * A picture for the category: the store's own, else the one the original site shows for it
   * (adr/0049), else the main image of the newest visible product in it or below it; null when
   * there is none of these.
   */
  image: storefrontImageSchema.nullable(),
});
export type StorefrontCategory = z.infer<typeof storefrontCategorySchema>;

export const storefrontCategoriesSchema = z.object({ items: z.array(storefrontCategorySchema) });

/**
 * A price a shopper may read: the store's list price in agorot, VAT included, and the higher
 * price it replaced when the store set one.
 */
export const storefrontPriceSchema = z.object({
  amountMinor: z.number().int().min(0),
  compareAtMinor: z.number().int().min(0).nullable(),
});
export type StorefrontPrice = z.infer<typeof storefrontPriceSchema>;

/**
 * A product as shoppers see it: what the existing store's product page shows, and nothing more.
 * The manufacturer, the model and the barcode are never part of it (adr/0024).
 */
export const storefrontProductCardSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  sku: z.string(),
  image: storefrontImageSchema.nullable(),
  /**
   * The manufacturer logo, as Avrahami's own store shows it on a product, or the one the store
   * chose instead (adr/0041); null without one.
   */
  logo: storefrontImageSchema.nullable(),
  /** False only for tracked stock that has run out. */
  available: z.boolean(),
  /** Null unless the store shows prices (adr/0024); the store still quotes on request. */
  price: storefrontPriceSchema.nullable(),
});
export type StorefrontProductCard = z.infer<typeof storefrontProductCardSchema>;

/**
 * Where a catalog product comes from, for the Product JSON-LD only (IMPLEMENTATION_PLAN Q2:
 * self-canonical, attribution in structured data). Never shown on the page.
 */
export const productAttributionSchema = z.object({
  name: z.string(),
  url: z.url().nullable(),
});
export type ProductAttribution = z.infer<typeof productAttributionSchema>;

export const storefrontProductSchema = storefrontProductCardSchema.extend({
  description: z.string(),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  specs: z.array(specRowSchema),
  /**
   * The product's strengths (adr/0048): written for this store alone with its copy, from the
   * original site's strengths of the product, so no two stores show the same ones. A product
   * whose strengths are not written yet shows its flyer's selling points (docs/FLYERS.md), which
   * have a title only. `icon` is one of FLYER_ICONS. Empty when it has neither.
   */
  benefits: z.array(z.object({ icon: z.string(), title: z.string(), text: z.string().nullable() })),
  /**
   * The businesses the product suits best ("מתאים במיוחד ל...") and its care and cleaning
   * instructions (adr/0054): written for this store alone with its copy, from the original
   * site's lines of the product. Empty until they are written, or when the original has none.
   */
  idealFor: z.array(z.string()),
  care: z.array(z.string()),
  images: z.array(storefrontImageSchema),
  /** Most a shopper can ask for now; null when stock is not tracked. */
  maxQuantity: z.number().int().min(0).nullable(),
  /** The path from a top category down to the product's category (for breadcrumbs). */
  breadcrumb: z.array(z.object({ id: z.uuid(), name: z.string() })),
  attribution: productAttributionSchema.nullable(),
  updatedAt: z.iso.datetime(),
  related: z.array(storefrontProductCardSchema),
});
export type StorefrontProduct = z.infer<typeof storefrontProductSchema>;

export const STOREFRONT_PAGE_SIZE = 24;

export const storefrontProductQuerySchema = z.object({
  category: z.uuid().optional(),
  q: z.string().trim().min(2).max(100).optional(),
  sort: storefrontSortSchema.default('newest'),
  page: z.coerce.number().int().min(1).max(1000).default(1),
});
export type StorefrontProductQuery = z.infer<typeof storefrontProductQuerySchema>;

export const storefrontProductPageSchema = z.object({
  items: z.array(storefrontProductCardSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type StorefrontProductPage = z.infer<typeof storefrontProductPageSchema>;

export const storefrontHomeSchema = z.object({
  /**
   * The opening banner (written by the AI for the store, adr/0034, and editable by the owner);
   * null fields fall back to the storefront's own words. `eyebrow` is the short line above the
   * title, `highlight` the title's second line in the store's colour.
   */
  hero: z.object({
    eyebrow: z.string().nullable(),
    title: z.string().nullable(),
    highlight: z.string().nullable(),
    text: z.string().nullable(),
    buttonLabel: z.string().nullable(),
    buttonLink: z.string().nullable(),
  }),
  /** The slider, in order (active slides only). */
  slides: z.array(
    z.object({
      id: z.uuid(),
      image: storefrontImageSchema,
      title: z.string().nullable(),
      text: z.string().nullable(),
      buttonLabel: z.string().nullable(),
      link: z.string().nullable(),
    }),
  ),
  /** The categories the owner chose to show, in order; empty means "the main categories". */
  featuredCategories: z.array(storefrontCategorySchema),
  /** Empty when the owner turned the newest-products row off. */
  newest: z.array(storefrontProductCardSchema),
});
export type StorefrontHome = z.infer<typeof storefrontHomeSchema>;

export const storefrontContentPageSchema = z.object({
  slug: z.string(),
  kind: contentPageKindSchema,
  title: z.string(),
  /** Plain text; blank lines separate paragraphs. Rendered as text, never as HTML. */
  body: z.string(),
  updatedAt: z.iso.datetime(),
});
export type StorefrontContentPage = z.infer<typeof storefrontContentPageSchema>;

export const CONTENT_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const contentPageSlugSchema = z.string().max(60).regex(CONTENT_PAGE_SLUG_PATTERN);

/** Everything the sitemap lists; capped well below the 50,000 URLs of one sitemap file. */
export const SITEMAP_MAX_PRODUCTS = 40_000;
export const storefrontSitemapSchema = z.object({
  products: z.array(z.object({ id: z.uuid(), updatedAt: z.iso.datetime() })),
  categories: z.array(z.object({ id: z.uuid() })),
  pages: z.array(z.object({ slug: z.string(), updatedAt: z.iso.datetime() })),
});
export type StorefrontSitemap = z.infer<typeof storefrontSitemapSchema>;

// ---------------------------------------------------------------------------------------------
// Cart and checkout
// ---------------------------------------------------------------------------------------------

export const CART_MAX_LINES = 50;
export const CART_MAX_QUANTITY = 99;

export const cartItemSchema = z.object({
  productId: z.uuid(),
  quantity: z.number().int().min(1).max(CART_MAX_QUANTITY),
});
export type CartItem = z.infer<typeof cartItemSchema>;

export const cartItemsSchema = z
  .array(cartItemSchema)
  .max(CART_MAX_LINES)
  .refine(
    (items) => new Set(items.map((item) => item.productId)).size === items.length,
    'duplicate product',
  );

/** Checking a cart: which of its lines can be requested now. */
export const cartCheckRequestSchema = z.object({ items: cartItemsSchema });
export type CartCheckRequest = z.infer<typeof cartCheckRequestSchema>;

export const cartLineSchema = z.object({
  productId: z.uuid(),
  title: z.string(),
  sku: z.string(),
  image: storefrontImageSchema.nullable(),
  quantity: z.number().int().min(1),
  maxQuantity: z.number().int().min(0).nullable(),
  /** The unit's list price; null unless the store shows prices (adr/0024). */
  price: storefrontPriceSchema.nullable(),
});
export type CartLine = z.infer<typeof cartLineSchema>;

export const cartIssueSchema = z.object({
  productId: z.uuid(),
  problem: cartProblemSchema,
  /** For INSUFFICIENT_STOCK: how many can be requested now. */
  available: z.number().int().min(0).nullable(),
});
export type CartIssue = z.infer<typeof cartIssueSchema>;

/** The lines that can be requested and the issues of those that cannot. */
export const cartCheckSchema = z.object({
  lines: z.array(cartLineSchema),
  issues: z.array(cartIssueSchema),
});
export type CartCheck = z.infer<typeof cartCheckSchema>;

const requiredText = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null);

/** Israeli phone numbers as people type them: digits, spaces, dashes, a leading +. */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9][0-9 -]{7,18}[0-9]$/, 'invalid phone number');

export const orderContactSchema = z.object({
  fullName: requiredText(120),
  email: emailSchema,
  phone: phoneSchema,
});
export type OrderContact = z.infer<typeof orderContactSchema>;

/** An address as stored and shown. */
export const orderAddressSchema = z.object({
  city: z.string(),
  street: z.string(),
  houseNumber: z.string(),
  apartment: z.string().nullable(),
  zip: z.string().nullable(),
});
export type OrderAddress = z.infer<typeof orderAddressSchema>;

/**
 * Sending a quote request: the products and quantities, and who asks. It carries no prices, no
 * payment and no way of receiving the goods: the store settles that with the shopper in its
 * quote. The store reads its own products again.
 */
export const placeOrderRequestSchema = z.object({
  /** Generated when the request page is shown; a repeated submit returns the same request. */
  idempotencyKey: z.uuid(),
  items: cartItemsSchema.refine((items) => items.length > 0, 'the cart is empty'),
  contact: orderContactSchema,
  notes: optionalText(1000),
});
export type PlaceOrderRequest = z.infer<typeof placeOrderRequestSchema>;

export const orderItemSchema = z.object({
  productId: z.uuid(),
  title: z.string(),
  sku: z.string(),
  quantity: z.number().int(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

export const placedOrderSchema = z.object({
  orderId: z.uuid(),
  number: z.number().int(),
  status: orderStatusSchema,
  /** The lines as they were recorded, for the message the shopper may send on WhatsApp. */
  items: z.array(orderItemSchema),
});
export type PlacedOrder = z.infer<typeof placedOrderSchema>;

// ---------------------------------------------------------------------------------------------
// Customers (the store's shoppers; a separate realm from staff, scoped to one store)
// ---------------------------------------------------------------------------------------------

export const customerProfileSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
});
export type CustomerProfile = z.infer<typeof customerProfileSchema>;

export const customerRegisterRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(256),
  fullName: requiredText(120),
  phone: phoneSchema.nullable().default(null),
});
export type CustomerRegisterRequest = z.infer<typeof customerRegisterRequestSchema>;

export const customerLoginRequestSchema = z.object({
  // Not validated as an email: a malformed value is just another failed login (no oracle).
  email: z.string().trim().toLowerCase().min(1).max(254),
  password: z.string().min(1).max(256),
});
export type CustomerLoginRequest = z.infer<typeof customerLoginRequestSchema>;

/**
 * A customer session. The token goes to the storefront server, which keeps it in an HttpOnly
 * cookie on the store's host; browsers never see the API.
 */
export const customerSessionSchema = z.object({
  token: z.string(),
  expiresAt: z.iso.datetime(),
  customer: customerProfileSchema,
});
export type CustomerSession = z.infer<typeof customerSessionSchema>;

export const updateCustomerProfileRequestSchema = z
  .object({
    fullName: requiredText(120),
    phone: phoneSchema.nullable(),
  })
  .partial();
export type UpdateCustomerProfileRequest = z.infer<typeof updateCustomerProfileRequestSchema>;

/** A shopper's own quote request, as the shopper sees it: never with a price. */
export const customerOrderSummarySchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  status: orderStatusSchema,
  itemCount: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type CustomerOrderSummary = z.infer<typeof customerOrderSummarySchema>;

export const customerOrdersSchema = z.object({ items: z.array(customerOrderSummarySchema) });

export const customerOrderSchema = customerOrderSummarySchema.extend({
  contact: orderContactSchema,
  /** Only on requests sent while the form still asked how to receive the goods; null since. */
  fulfillment: fulfillmentMethodSchema.nullable(),
  address: orderAddressSchema.nullable(),
  notes: z.string().nullable(),
  items: z.array(orderItemSchema),
});
export type CustomerOrder = z.infer<typeof customerOrderSchema>;
